import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import '../styles/dashboard.css';
import AppSidebar from '../components/layout/AppSidebar';
import NewDashboardView from '../components/views/dashboard/NewDashboardView';
import DashboardWorkspaceView from '../components/views/dashboard/DashboardWorkspaceView';
import SpinningLoader from '../components/ui/SpinningLoader';
import Toast from '../components/ui/Toast';
import { Project } from '../types/project';
import { projectsAPI, CreateProjectRequest } from '../services/projectsApi';
import DashboardProjectsView from '../components/views/dashboard/DashboardProjectsView';
import RenameProjectDialog from '../components/modals/RenameProjectDialog';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [, setProjectError] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('error');
  const [lastCreatedProject, setLastCreatedProject] = useState<Project | null>(null);
  const [triggerDeleteProject, setTriggerDeleteProject] = useState<Project | null>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [, setIsSidenavExpanded] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [isAgentMode, setIsAgentMode] = useState<boolean>(true); // true = research, false = chat

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  // No ⌘/Ctrl+N shortcut for new projects: browsers reserve Ctrl+N (new
  // window) and pages cannot preventDefault it, so a shortcut here would need
  // a different, unreserved key combination (product decision).

  // Load projects from API
  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const projectsList = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(projectsList);
    } catch (err) {
      console.error('Error loading projects:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
      setError(errorMessage);
      showToast('Something went wrong. Failed to fetch projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load projects when component mounts
  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run-once mount effect; loadProjects is recreated each render and depending on it would refetch on every render
  }, []);

  // Reload projects when navigating to pages with ProjectGrid
  useEffect(() => {
    if (
      location.pathname === '/dashboard' ||
      location.pathname === '/' ||
      location.pathname === '/dashboard/projects' ||
      location.pathname === '/dashboard/create'
    ) {
      loadProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- must only refetch on navigation; loadProjects is recreated each render and depending on it would refetch on every render
  }, [location.pathname]);

  // Effect to handle URL changes and load project data
  useEffect(() => {
    console.log(id);
    if (id) {
      const loadProject = async () => {
        setProjectLoading(true);
        setProjectError(null);
        setSelectedProject(undefined); // Clear previous project

        try {
          const project = await projectsAPI.getProject(id);
          setSelectedProject(project);
        } catch (err) {
          console.error('Error loading project:', err);

          // Always redirect to dashboard home on project loading error
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000); // Small delay to let user see the toast

          setProjectError('Failed to load project');
        } finally {
          setProjectLoading(false);
        }
      };

      loadProject();
    } else {
      setSelectedProject(undefined);
      setProjectError(null);
      setProjectLoading(false);
    }
  }, [id, navigate]); // Removed projects dependency as we no longer use it as fallback

  const handleCreateClick = () => {
    // Don't open dialog if already on create page
    const path = location.pathname;
    if (path === '/dashboard/create') {
      return;
    }

    // Open dialog to get project name
    setNameDialogOpen(true);
  };

  const handleNameCancel = () => {
    setNameDialogOpen(false);
  };

  const handleNameConfirm = async (name: string) => {
    setNameDialogOpen(false);
    setCurrentProjectName(name);

    // Create the project first
    await handleSaveProject({ name });

    // Then navigate to create page
    navigate('/dashboard/create', {
      replace: true  // Replace current history entry to prevent back button issues
    });
  };

  const handleRenameProjectName = async (newName: string) => {
    setCurrentProjectName(newName);

    // If we have a created project, update it in the database
    if (lastCreatedProject) {
      try {
        await projectsAPI.updateProject(lastCreatedProject.project_id, {
          name: newName
        });

        // Update the local project object
        setLastCreatedProject({
          ...lastCreatedProject,
          name: newName
        });

        // Refresh projects list
        const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
        setProjects(updatedProjects);
      } catch (err) {
        console.error('Error renaming project:', err);
        showToast('Failed to rename project', 'error');
      }
    }
  };

  const handleAgentModeChange = (value: boolean) => {
    console.log('[Dashboard] Mode changed to:', value ? 'research' : 'chat');
    setIsAgentMode(value);
  };

  const handleViewProject = (project: Project) => {
    // When viewing a project, set mode from project if available
    if (project.mode) {
      setIsAgentMode(project.mode === 'research');
    }
    if (project.is_draft) {
      // For draft projects, navigate to create page and set the project
      setLastCreatedProject(project);
      setCurrentProjectName(project.name);
      navigate('/dashboard/create');
    } else {
      // For non-draft projects, navigate to project view
      navigate(`/dashboard/project/${project.project_id}`);
    }
  };

  const handleCreateProject = () => {
    // Don't open dialog if already on create page
    const path = location.pathname;
    if (path === '/dashboard/create') {
      return;
    }

    // Open dialog to get project name
    setNameDialogOpen(true);
  };

  const handleBackToHome = () => {
    navigate('/dashboard');
  };

  const handleSaveProject = async (projectData: Partial<Project>) => {
    try {
      const createRequest: CreateProjectRequest = {
        name: projectData.name || 'New Project',
        description: projectData.description || '',
        initial_query: projectData.initial_query || '',
        metadata: projectData.metadata || {},
        mode: isAgentMode ? 'research' : 'chat'
      };

      const newProject = await projectsAPI.createProject(createRequest);

      // Store the created project for undo functionality
      setLastCreatedProject(newProject);

      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);

      // Show success toast
      showToast(`"${newProject.name}" has been successfully created`, 'success');

      // Collapse the sidenav when navigating after project creation
      setIsSidenavExpanded(false);
    } catch (err) {
      console.error('Error creating project:', err);
      showToast('Something went wrong. Failed to create new project', 'error');
      // Go back to home on error
      handleBackToHome();
    }
  };

  const handleCreateProjectWithQuestion = async (name: string, question: string, attachedFiles: File[]) => {
    try {
      // Create project with initial question
      const createRequest: CreateProjectRequest = {
        name: name ? name : `${question.substring(0, 50)}${question.length > 50 ? '...' : ''}`,
        description: question,
        initial_query: question,
        metadata: {
          attachedFiles: attachedFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))
        },
        mode: isAgentMode ? 'research' : 'chat'
      };

      const newProject = await projectsAPI.createProject(createRequest);

      // Store the created project for undo functionality
      setLastCreatedProject(newProject);

      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);

      // Collapse the sidenav when navigating to project
      setIsSidenavExpanded(false);

      // Navigate to the new project with flag to auto-submit query only after successful creation
      navigate(`/dashboard/project/${newProject.project_id}`, {
        state: {
          initialQuestion: question,
          initialAttachments: attachedFiles,
          autoSubmitQuery: true, // Flag to indicate automatic query submission
          initialEntryId: newProject.initial_entry_id // Entry ID created synchronously (fix for #12213)
        },
        replace: true // Replace the create page in history to prevent back navigation
      });
    } catch (err) {
      console.error('Error creating project with question:', err);
      showToast('Something went wrong. Failed to create new project', 'error');
      handleBackToHome();
      throw err; // Re-throw so the calling component can handle the error
    }
  };

  const handleRenameProject = async (project: Project, newName: string) => {
    try {
      await projectsAPI.updateProject(project.project_id, { name: newName });
      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);
    } catch (err) {
      console.error('Error renaming project:', err);
      throw err; // Re-throw so the component can handle the error state
    }
  };

  const handleDeleteProject = async (project: Project) => {
    try {
      await projectsAPI.deleteProject(project.project_id);
      // Refresh projects list
      const updatedProjects = await projectsAPI.listProjects({ status: 'active', limit: 100 });
      setProjects(updatedProjects);

      // Clear the last created project if it was deleted
      if (lastCreatedProject && lastCreatedProject.project_id === project.project_id) {
        setLastCreatedProject(null);
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      throw err; // Re-throw so the modal can handle the error state
    }
  };

  const handleUndoCreateProject = () => {
    if (lastCreatedProject) {
      setToastVisible(false);
      // Navigate to home first if not already there
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      // Trigger the delete modal for the last created project
      setTriggerDeleteProject(lastCreatedProject);
    }
  };

  const renderCurrentView = () => {
    const path = location.pathname;
    console.log('🔍 Dashboard renderCurrentView - path:', path);
    console.log('🔍 Loading state:', loading);
    console.log('🔍 Projects count:', projects.length);

    // Handle /dashboard/projects route - allow it to render even while loading
    if (path === '/dashboard/projects') {
      console.log('✅ Rendering DashboardProjectsView');
      return (
        <DashboardProjectsView
          projects={projects}
          onCreateProject={handleCreateProject}
          onViewProject={handleViewProject}
          onRenameProject={handleRenameProject}
          onDeleteProject={handleDeleteProject}
          triggerDeleteProject={triggerDeleteProject}
          onDeleteProjectTriggered={() => setTriggerDeleteProject(null)}
        />
      );
    }

    // Show loading spinner for other routes
    if (loading) {
      return (
        <div className="dashboard-content">
          <div className="dashboard-loading">
            <SpinningLoader size="large" text="Loading dashboard..." />
          </div>
        </div>
      );
    }

    // Remove error state - let the home page load with empty projects
 else if (path === '/dashboard/create') {
      return (
        <NewDashboardView
          isCreating={true}
          project={lastCreatedProject || undefined}
          projectName={currentProjectName}
          onBack={handleBackToHome}
          onSave={handleSaveProject}
          onCreateWithQuestion={handleCreateProjectWithQuestion}
          onRenameProjectName={handleRenameProjectName}
          onResetProject={() => {
            setLastCreatedProject(null);
            setCurrentProjectName('');
          }}
          isAgentMode={isAgentMode}
          onAgentModeChange={handleAgentModeChange}
        />
      );
    } else if (path.startsWith('/dashboard/project/')) {
      // Handle project loading states
      if (projectLoading) {
        return (
          <div className="dashboard-content">
            <div className="dashboard-loading">
              <SpinningLoader size="large" text="Loading project..." />
            </div>
          </div>
        );
      }

      console.log('Selected project:', selectedProject);
      if (!selectedProject) {
        // This case should rarely be hit since errors now redirect automatically
        return (
          <div className="dashboard-content">
            <div className="dashboard-loading">
              <SpinningLoader size="large" text="Redirecting..." />
            </div>
          </div>
        );
      }

      return (
        <DashboardWorkspaceView
          project={selectedProject}
          onRenameProject={handleRenameProject}
        />
      );
    } else if (path === '/dashboard/home' || path === '/dashboard' || path === '/' || path === '/dashboard/projects') {
      // Default home view - show all projects page
      return (
        <DashboardProjectsView
          projects={projects}
          onCreateProject={handleCreateClick}
          onViewProject={handleViewProject}
          onDeleteProject={handleDeleteProject}
          onRenameProject={handleRenameProject}
          triggerDeleteProject={triggerDeleteProject}
          onDeleteProjectTriggered={() => setTriggerDeleteProject(null)}
        />
      );
    } else {
      // Fallback for any other routes
      return (
        <NewDashboardView
          isCreating={false}
          projects={projects}
          title='Home'
          projectName={currentProjectName}
          onBack={handleBackToHome}
          onSave={handleSaveProject}
          onCreateWithQuestion={handleCreateProjectWithQuestion}
          onViewProject={handleViewProject}
          onRenameProject={handleRenameProject}
          onDeleteProject={handleDeleteProject}
          onRenameProjectName={handleRenameProjectName}
          onResetProject={() => {
            setLastCreatedProject(null);
            setCurrentProjectName('');
          }}
        />
      );
    }
  };

  return (
    <>
      <div className="aspire-dashboard">
        {/* Sidebar */}
        <AppSidebar />

        {/* Main Content */}
        {renderCurrentView()}

        {/* Toast for error messages */}
        <Toast
          message={toastMessage}
          type={toastType}
          isVisible={toastVisible}
          onClose={hideToast}
          duration={4000}
          actionLabel={toastType === 'success' && lastCreatedProject ? 'Undo' : undefined}
          onAction={toastType === 'success' && lastCreatedProject ? handleUndoCreateProject : undefined}
        />

      </div>

      <RenameProjectDialog
        isOpen={nameDialogOpen}
        onClose={handleNameCancel}
        onRename={handleNameConfirm}
        dialogTitle='Project Name'
        isCreate={true}
        projectName={''}
      />
    </>
  );
};

export default Dashboard;